import * as z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useCreateRole, usePermissions } from '../hooks/use-roles'
import { PermissionsSelector } from './permissions-selector'

const createRoleSchema = z.object({
  name: z
    .string()
    .min(1, 'Role name is required')
    .max(50, 'Role name must not exceed 50 characters'),
  description: z
    .string()
    .max(255, 'Description must not exceed 255 characters')
    .optional(),
  permissions: z.array(z.string()),
})

type CreateRoleFormValues = z.infer<typeof createRoleSchema>

interface CreateRoleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateRoleDialog({
  open,
  onOpenChange,
}: CreateRoleDialogProps) {
  const { t } = useTranslation()
  const { mutate: createRole, isPending } = useCreateRole()
  const { data: allPermissions, isLoading: isLoadingPermissions } =
    usePermissions()

  const form = useForm<CreateRoleFormValues>({
    resolver: zodResolver(createRoleSchema),
    defaultValues: {
      name: '',
      description: '',
      permissions: [],
    },
  })

  const onSubmit = (data: CreateRoleFormValues) => {
    createRole(
      {
        name: data.name,
        description: data.description || undefined,
        permissions: data.permissions,
      },
      {
        onSuccess: () => {
          form.reset()
          onOpenChange(false)
        },
      }
    )
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset()
    }
    onOpenChange(newOpen)
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className='sm:max-w-xl p-0 overflow-hidden flex flex-col h-full' dir={t('dir') as 'ltr' | 'rtl'}>
        <SheetHeader className='p-6'>
          <SheetTitle>{t('roles.dialog.create.title', 'Create Role')}</SheetTitle>
          <SheetDescription>
            {t(
              'roles.dialog.create.description',
              'Create a new role with custom permissions. Role names must be uppercase.'
            )}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className='flex flex-col flex-1 min-h-0'
          >
            <div className='flex-1 space-y-6 px-6 overflow-y-auto pb-6'>
              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('roles.dialog.roleName', 'Role Name')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('roles.dialog.roleNamePlaceholder', 'MANAGER')}
                        {...field}
                        disabled={isPending}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='description'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('roles.dialog.description', 'Description')} ({t('common.optional', 'Optional')})
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t(
                          'roles.dialog.descriptionPlaceholder',
                          'Manager role with specific permissions'
                        )}
                        rows={3}
                        {...field}
                        disabled={isPending}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='permissions'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('roles.dialog.permissions', 'Permissions')}</FormLabel>
                    <FormControl>
                      {isLoadingPermissions ? (
                        <div className='space-y-3'>
                          <Skeleton className='h-10 w-full' />
                          <Skeleton className='h-64 w-full' />
                        </div>
                      ) : allPermissions ? (
                        <PermissionsSelector
                          allPermissions={allPermissions}
                          selectedPermissions={field.value}
                          onChange={field.onChange}
                          disabled={isPending}
                        />
                      ) : (
                        <p className='text-muted-foreground text-sm'>
                          {t('roles.dialog.noPermissions', 'No permissions available')}
                        </p>
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <SheetFooter className='flex-row gap-2 shrink-0 border-t p-6 bg-background'>
              <Button
                type='button'
                variant='outline'
                onClick={() => handleOpenChange(false)}
                disabled={isPending}
                className='flex-1 sm:flex-1'
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                type='submit'
                disabled={isPending}
                className='flex-1 sm:flex-1'
              >
                {isPending ? t('common.creating', 'Creating...') : t('roles.createRole', 'Create Role')}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}
